import React, { useContext } from 'react';
import { useActor } from '@xstate/react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import useQueryParams from '../hooks/useQueryParams';
import SnackbarContext from '../context/SnackbarContext';
import Page from '../components/page/Page';
import PageContent from '../components/page/PageContent';
import { useDialog } from '../context/DialogContext';
import { withAuthRequired } from '../services/buildfire/auth';
import {
  SubmitCaseContextProvider,
  useSubmitCaseContext,
} from '../context/SubmitCaseContext';
import EnterInfo from '../components/submitCase/EnterInfo';
import CreditCard from '../components/submitCase/CreditCard';
import Checkout from '../components/submitCase/Checkout';
import ModalLoader from '../components/common/ModalLoader';
import { useNavBlock } from '../services/buildfire/history/useBreadcrumbs';
import CaseContext from '../services/buildfire/rdb/CaseContext';
import TrackView from '../components/analytics/TrackView';
import { VIEW_EVENTS } from '../services/buildfire/analytics/events';
import { useFirebase } from '../services/firebase';
import {
  IonContent,
  IonHeader,
  IonPage,
  useIonModal,
  useIonToast,
} from '@ionic/react';
import Toolbar from '../components/common/Toolbar';
import AlertBanner from '../components/common/AlertBanner';
import EmailVerificationRequiredModal from '../components/modals/EmailVerificationRequiredModal';
import SelectPaymentType from '../components/submitCase/components/SelectPaymentType';
import { getErrorMessage } from '../util/error';
import CaseTypeSelection from '../components/submitCase/CaseTypeSelection';
import Typography from '../components/common/Typography';
import { DialogActionButton } from '../components/common/Dialog';

interface SubmitCaseProps {}

const Container = styled.div`
  // padding: 8px;
`;

const StyledPage = styled(Page)`
  align-items: center;
`;

const StyledPageContent = styled(PageContent)`
  width: 100%;
  max-width: 600px;
  > div {
    width: 100%;
  }
`;

export function useEmailVerificationWarning() {
  const { currentUser, app } = useFirebase();
  const history = useHistory();
  const [present, dismiss] = useIonModal(EmailVerificationRequiredModal, {
    app,
    user: currentUser,
    onClose: () => {
      dismiss();
    },
  });

  React.useEffect(() => {
    if (currentUser?.emailVerified || process.env.NODE_ENV === 'development') {
      return;
    }

    present({
      onDidDismiss: () => {
        history.replace({
          pathname: '/',
        });
      },
    });

    return () => {
      dismiss();
    };
  }, [currentUser?.uid]);
}

const SubmitCase: React.FC<SubmitCaseProps> = ({}) => {
  const { currentUser, parentAccount } = useFirebase();
  const history = useHistory();
  const { openDialogPromise, openDialog } = useDialog();
  const { setMessage } = useContext(SnackbarContext);
  const { submitCaseService } = useSubmitCaseContext();
  const [state, send] = useActor(submitCaseService);
  const { addCase } = useContext(CaseContext);
  const [present] = useIonToast();

  // Keep parent account in sync since this value might be grabbed after SubmitCaseContext is mounted
  React.useEffect(() => {
    send({ type: 'PARENT_ACCOUNT_CHANGE', parentAccount });
  }, [parentAccount?.uid]);

  // Require email verification before submitting a case
  useEmailVerificationWarning();

  React.useEffect(() => {
    const onDone = () => {
      const { id } = submitCaseService.state.context.createdCase!;
      addCase(submitCaseService.state.context.createdCase!);
      history.replace(`/cases/${id}`);
      setMessage({ text: 'Case successfully created' });
    };
    submitCaseService.onDone(onDone);
    return () => {
      submitCaseService.off(onDone);
    };
  }, []);

  React.useEffect(() => {
    const { error } = state.context;
    if (error) {
      openDialogPromise(<div>{getErrorMessage(error)}</div>);
    }
  }, [state.context.error]);

  useNavBlock(({ action }) => {
    if (action !== 'POP') {
      return true;
    }

    if (state.matches('caseType')) {
      // Allow normal history pop on initial screen
      return true;
    }

    // Otherwise navigate BACK in the state machine
    send({ type: 'BACK' });
    return false;
  });

  if (!currentUser) {
    throw new Error('ERROR not logged in');
  }

  const spreadProps = { currentUser };

  // Show draft load confirmation dialog
  React.useEffect(() => {
    if (state.matches('draftLoadConfirmation')) {
      const draft = state.context.pendingDraft;
      if (draft) {
        const draftDate = new Date(
          draft.updatedAt || draft.createdAt
        ).toLocaleDateString();
        const draftTime = new Date(
          draft.updatedAt || draft.createdAt
        ).toLocaleTimeString();

        openDialog(
          <Typography fontSize="16px">
            Load draft from {draftDate} at {draftTime}?
          </Typography>,
          {
            onClose: (action) => {
              if (action === 'accept') {
                send({ type: 'LOAD_DRAFT' });
              } else {
                send({ type: 'CLEAR_DRAFT' });
              }
            },
            actions: (
              <>
                <DialogActionButton action="close" emphasis="low">
                  Clear and Start Over
                </DialogActionButton>
                <DialogActionButton action="accept">
                  Load Draft
                </DialogActionButton>
              </>
            ),
          }
        );
      }
    }
  }, [state.value]);

  // Show toast when draft is saved and user navigates away
  React.useEffect(() => {
    return () => {
      // Only show toast if we're leaving during case submission (not after completion)
      if (
        state.context.draftId &&
        !state.matches('checkout.success') &&
        !state.context.createdCase
      ) {
        present({
          message: 'Case saved as draft',
          duration: 3000,
          position: 'bottom',
        });
      }
    };
  }, []);

  return (
    <StyledPage>
      <StyledPageContent>
        <Container>
          {state.matches('caseType') && <CaseTypeSelection {...spreadProps} />}
          {state.matches('enterInfo') && <EnterInfo {...spreadProps} />}
          {state.matches('savingDraft') && (
            <ModalLoader
              ProgressBarProps={{
                indeterminate: !state.context.uploadProgress?.percent,
                progress: state.context.uploadProgress?.percent,
              }}
              progressText={state.context.uploadProgress?.message || 'Saving draft...'}
              onClose={() => send({ type: 'BACK' })}
            />
          )}
          {state.matches('selectPaymentType') && (
            <SelectPaymentType {...spreadProps} />
          )}
          {state.matches('creditCard') && <CreditCard {...spreadProps} />}
          {state.matches('checkout') && <Checkout {...spreadProps} />}
        </Container>
      </StyledPageContent>
    </StyledPage>
  );
};

const WrappedSubmitCase: React.FC<{}> = () => {
  const queryParams = useQueryParams();
  const targetPage = queryParams.get('page') || undefined;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar showBackButton title="Submit Case" />
        <AlertBanner />
      </IonHeader>
      <IonContent>
        <SubmitCaseContextProvider targetPage={targetPage}>
          <TrackView name={VIEW_EVENTS.SUBMIT_CASE}>
            <SubmitCase />
          </TrackView>
        </SubmitCaseContextProvider>
      </IonContent>
    </IonPage>
  );
};

export default withAuthRequired(WrappedSubmitCase);
